import {
  Component,
  OnInit,
  OnDestroy,
  Output,
  EventEmitter,
  Input,
} from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { Subscription } from 'rxjs';
import { AuthService } from 'shared-lib';
import { Language } from 'shared-lib';
import jwt_decode from 'jwt-decode';
import { TranslationService } from '../../services/translation.service';
import { UtilityLanguageSuffix } from 'shared-lib';
import { TranslateService } from '@ngx-translate/core';
import { PermissionsService } from 'shared-lib';

@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
  standalone: false,
})
export class SidebarComponent implements OnInit, OnDestroy {
  @Input() sidebarExpanded: boolean = false;

  activeItem: string = '';
  tooltipVisible: boolean = false;
  loggedUser: string = '--';
  username: string = '';
  tooltipText: string = '';
  utilityIcon: string = '';
  selectedLanguage: string;
  showLanguageDropdown = false;
  hoveredItem: string = '';
  private routeSubscription: Subscription;

  constructor(
    private router: Router,
    private authService: AuthService,
    private translate: TranslateService,
    public permissionService: PermissionsService
  ) {
    this.routeSubscription = this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.updateActiveItem();
      }
    });
  }

  ngOnInit(): void {
    this.updateActiveItem(); // Set initial active item on page load/refresh
    this.getUtilityIcon();
    let langIndex = Number.parseInt(
      localStorage.getItem('preferredLanguage') ?? '0'
    );
    this.selectedLanguage = langIndex === Language.English ? 'en' : 'fr';

    let user: any = jwt_decode(localStorage.getItem('token') ?? '');
    if (user != undefined) {
      var firstNameLetter = user['name'].split(' ')[0];
      this.username = user['name'];
      var lastNameLetter = user['name'].split(' ')[1];
      this.loggedUser = firstNameLetter[0]
        ? firstNameLetter[0]
        : '' + lastNameLetter[0]
        ? lastNameLetter[0]
        : '';
    }
  }

  getUtilityIcon() {
    this.utilityIcon = localStorage.getItem('logoUrl') ?? '';
  }

  ngOnDestroy(): void {
    this.routeSubscription.unsubscribe();
  }

  private updateActiveItem(): void {
    const currentRoute = this.router.url;

    const routeParts = currentRoute.split('/').filter((part) => part);
    const primaryRoute = routeParts[0] || '';

    // List of valid sidebar routes
    const validRoutes = [
      'map',
      'dashboard',
      'ai',
      'network-assets',
      'customers',
      'asset-list',
      'operations',
      'meter-management',
      'readings',
      'settings',
      'dashboard-bps',
      'job-history',
      'assets',
      'audit-log',
      'utility-management',
    ];

    this.activeItem = validRoutes.includes(primaryRoute) ? primaryRoute : '';
  }

  onNavClick(route: string) {
    this.activeItem = route;
    this.router.navigate([route]);
  }

  // Placeholder for customers (remove when customers page is implemented)
  dummyClick(route: string) {
    this.activeItem = route;
  }

  logout() {
    this.authService.logout(true);
  }

  changeLanguage(language: Language) {
    localStorage.setItem('preferredLanguage', language.toString());
    this.showLanguageDropdown = false;

    this.authService.changePreferredLanguage(language).subscribe({
      next: (response) => {
        const suffixes = response.data ?? [];
        localStorage.setItem('languageSuffixes', JSON.stringify(suffixes));
        this.setLanguage(language, suffixes);
      },
      error: (_) => {
        const suffixes: UtilityLanguageSuffix[] = JSON.parse(
          localStorage.getItem('languageSuffixes') ?? '[]'
        );
        this.setLanguage(language, suffixes);
      },
    });
  }

  setLanguage(language: Language, suffixes: UtilityLanguageSuffix[]) {
    const fileName = this.getTranslationFileName(language, suffixes);
    this.translate.use(fileName);
    window.location.reload();
  }

  getTranslationFileName(
    language: Language,
    suffixes: UtilityLanguageSuffix[]
  ): string {
    this.selectedLanguage = language === Language.English ? 'en' : 'fr';
    const suffix = suffixes.find((x) => x.language === language)?.suffix || '';
    return `${this.selectedLanguage}${suffix}`;
  }

  onMouseEnter() {
    this.sidebarExpanded = true;
  }

  onMouseLeave() {
    this.sidebarExpanded = false;
    this.hoveredItem = '';
  }
}
